import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { JoinGameMessage, PitchMessage } from '../../../shared/types';

interface UseControllerWebSocketReturn {
  socket: Socket | null;
  connected: boolean;
  error: string | null;
  reconnecting: boolean;
  joinGame: (gameId: string, deviceId: string) => void;
  sendPitchData: (gameId: string, deviceId: string, pitch: number) => void;
}

export function useControllerWebSocket(serverUrl: string): UseControllerWebSocketReturn {
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second

  // Pitch data transmission optimization
  const lastPitchDataRef = useRef<{ pitch: number; timestamp: number } | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const attemptReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      setError('Connection failed after multiple attempts');
      setReconnecting(false);
      return;
    }

    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
      30000 // Max 30 seconds
    );

    console.log(`Attempting reconnection ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts} in ${delay}ms`);
    setReconnecting(true);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }, delay);
  }, []);

  const initializeSocket = useCallback(() => {
    console.log('Controller attempting to connect to:', serverUrl);
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: true,
      autoConnect: true
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Controller connected to WebSocket server');
      setConnected(true);
      setReconnecting(false);
      setError(null);
      reconnectAttemptsRef.current = 0;
      clearReconnectTimeout();
    });

    socket.on('disconnect', (reason) => {
      console.log('Controller disconnected from WebSocket server:', reason);
      setConnected(false);

      // Only attempt reconnection for certain disconnect reasons
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
        setError('Disconnected by server');
      } else {
        // Network issues, attempt reconnection
        attemptReconnect();
      }
    });

    socket.on('connect_error', (err: any) => {
      console.error('Controller connection error:', err);
      setConnected(false);
      setError('Failed to connect to server');
      attemptReconnect();
    });

    socket.on('error', (err: any) => {
      console.error('Controller WebSocket error:', err);
      setError(err.message || 'Connection error');
    });

    return socket;
  }, [serverUrl, attemptReconnect, clearReconnectTimeout]);

  useEffect(() => {
    const socket = initializeSocket();

    return () => {
      clearReconnectTimeout();
      socket.disconnect();
    };
  }, [initializeSocket, clearReconnectTimeout]);

  const joinGame = useCallback((gameId: string, deviceId: string) => {
    if (socketRef.current && connected) {
      const message: JoinGameMessage = {
        type: 'joinGame',
        gameId,
        deviceId,
        clientType: 'controller'
      };

      console.log('Controller joining game:', message);
      socketRef.current.emit('message', message);
    }
  }, [connected]);

  // Optimized pitch data transmission with minimal filtering for responsiveness
  const sendPitchData = useCallback((gameId: string, deviceId: string, pitch: number) => {
    if (!socketRef.current || !connected) {
      return;
    }

    const now = Date.now();
    const lastData = lastPitchDataRef.current;

    // Only skip if pitch hasn't changed at all and it's been less than 50ms (for rate limiting)
    if (lastData &&
      Math.abs(pitch - lastData.pitch) < 0.5 &&
      (now - lastData.timestamp) < 50) {
      return;
    }

    const pitchMessage: PitchMessage = {
      type: 'pitch',
      gameId,
      deviceId,
      pitch: Math.round(pitch * 10) / 10, // Round to 1 decimal place to reduce message size
      timestamp: now
    };

    // Send immediately for responsiveness
    socketRef.current.emit('message', pitchMessage);

    lastPitchDataRef.current = { pitch, timestamp: now };
  }, [connected]);

  return {
    socket: socketRef.current,
    connected,
    reconnecting,
    error,
    joinGame,
    sendPitchData
  };
}